/**
 * API Service - Streaming API for AgenX
 * Handles stream-full-workflow endpoint with real-time updates
 */

import { 
  AccumulatedInputs, 
  StreamChunk, 
  StreamCallbacks, 
  TabVisibility,
  ProposalSubTabVisibility,
  VALID_PAYLOAD_KEYS
} from '../types/streaming'

// Re-export types for consumers
export type { ChatHistoryItem } from '../types/streaming'

const API_BASE_URL = 'http://0.0.0.0:8888/api/v1'
const STORAGE_KEY = 'accumulatedInputs'

// ============================================================================
// User Management
// ============================================================================

/**
 * Get user ID from localStorage or return empty string
 * Note: This is kept for backward compatibility with streaming API
 * For new code, use useAuth() hook to get user/admin info
 */
export function getUserId(): string {
  return localStorage.getItem('user_id') || ''
}

// ============================================================================
// Inputs Management
// ============================================================================

/**
 * Create empty inputs structure matching API expectations
 * Includes both valid API keys and UI-only state keys
 */
export function createEmptyInputs(): AccumulatedInputs {
  return {
    // === Valid API keys (matching backend types) ===
    // Global
    query: '',                          // str
    chat_history: [],                   // List[Dict[str, str]]
    status: '',                         // str
    pricing: [],                        // List[Dict[str, str]]
    // Orchestration
    next_node: '',                      // str
    reply_to_human: '',                 // str
    // Requirements and campaign
    clarification: '',                  // str
    continue_choice: false,             // bool (not string!)
    overview: '',                       // str
    objectives: [],                     // List[str]
    period: '',                         // str
    // Media plan
    context: '',                        // str
    media_plan: {},                     // Dict[str, Any]
    evaluation: {},                     // Dict[str, Any]
    // Image generation
    raw_image_data: [],                 // List[str]
    aspect_ratio: '',                   // str
    image_generation_instruction: '',   // str
    image_data: '',                     // str
    image_grading_feedback: {},         // Dict[str, Any]
    // Template
    brand_guidelines: '',               // str
    template_guidelines: '',            // str
    reference_images: [],               // list
    boilerplate_code: '',               // str
    html_code: '',                      // str
    validation: {},                     // Dict[str, Any]
    template_grading_output: {},        // Dict[str, Any]
    
    // === UI-only state keys (NOT sent to API) ===
    channels: [],
    budget: [],
    kpi: '',
    creative_ideas: '',
    campaign: {},                       // For UI detection of campaign response
    site_list: {},
    creative_generation: {},
    template_recommender: {},
    insights: {},
  }
}

/**
 * Load accumulated inputs from localStorage
 */
export function loadInputsFromStorage(): AccumulatedInputs {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    console.error('Failed to load inputs from storage:', e)
  }
  return createEmptyInputs()
}

/**
 * Save accumulated inputs to localStorage
 */
export function saveInputsToStorage(inputs: AccumulatedInputs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(inputs))
  } catch (e) {
    console.error('Failed to save inputs to storage:', e)
  }
}

/**
 * Clear accumulated inputs from localStorage
 */
export function clearInputsFromStorage(): void {
  localStorage.removeItem(STORAGE_KEY)
}

/**
 * Default values for each valid payload key (matching backend types)
 */
const DEFAULT_PAYLOAD_VALUES: Record<string, any> = {
  // Global
  query: '',                          // str
  chat_history: [],                   // List[Dict[str, str]]
  status: '',                         // str
  pricing: [],                        // List[Dict[str, str]]
  // Orchestration
  next_node: '',                      // str
  reply_to_human: '',                 // str
  // Requirements and campaign
  clarification: '',                  // str
  continue_choice: false,             // bool
  overview: '',                       // str
  objectives: [],                     // List[str]
  period: '',                         // str
  // Media plan
  context: '',                        // str
  media_plan: {},                     // Dict[str, Any]
  evaluation: {},                     // Dict[str, Any]
  // Image generation
  raw_image_data: [],                 // List[str]
  aspect_ratio: '',                   // str
  image_generation_instruction: '',   // str
  image_data: '',                     // str
  image_grading_feedback: {},         // Dict[str, Any]
  // Template
  brand_guidelines: '',               // str
  template_guidelines: '',            // str
  reference_images: [],               // list
  boilerplate_code: '',               // str
  html_code: '',                      // str
  validation: {},                     // Dict[str, Any]
  template_grading_output: {},        // Dict[str, Any]
}

/**
 * Sanitize inputs for API payload
 * Always includes ALL valid keys (with defaults for empty values)
 * Filters out UI-only state keys
 */
export function sanitizeInputsForPayload(inputs: AccumulatedInputs): Record<string, any> {
  const sanitized: Record<string, any> = {}
  
  // Always include all valid keys with their values or defaults
  for (const key of VALID_PAYLOAD_KEYS) {
    const value = inputs[key]
    // Use the value from inputs if it exists and is not undefined/null, otherwise use default
    if (value !== undefined && value !== null) {
      sanitized[key] = value
    } else {
      sanitized[key] = DEFAULT_PAYLOAD_VALUES[key]
    }
  }
  
  return sanitized
}

// ============================================================================
// Chunk Merging
// ============================================================================

/**
 * Deep merge a chunk into accumulated inputs
 * - Flattens orchestrator/requirements/campaign child keys into root level
 * - Stores agent results (media_plan, creative_generation, etc.) for UI display
 * - Arrays are replaced (chat_history, pricing)
 * - Objects are deep merged
 */
export function mergeChunkIntoInputs(
  accumulated: AccumulatedInputs, 
  chunk: StreamChunk
): AccumulatedInputs {
  const result = { ...accumulated }
  
  // Process orchestrator data - flatten child keys to root level
  if (chunk.orchestrator) {
    const orch = chunk.orchestrator
    if (orch.next_node) result.next_node = orch.next_node
    if (orch.reply_to_human) result.reply_to_human = orch.reply_to_human
    if (orch.chat_history) result.chat_history = orch.chat_history
    if (orch.pricing) result.pricing = orch.pricing
  }
  
  // Process requirements data - flatten child keys to root level
  if (chunk.requirements) {
    const req = chunk.requirements
    if (req.clarification) result.clarification = req.clarification
    if (req.continue_choice !== undefined) result.continue_choice = req.continue_choice
    if (req.chat_history) result.chat_history = req.chat_history
    if (req.status) result.status = req.status
    if (req.pricing) result.pricing = req.pricing
    // Flatten campaign-related fields
    if (req.overview) result.overview = req.overview
    if (req.objectives) result.objectives = req.objectives
    if (req.period) result.period = req.period
  }
  
  // Process campaign data - flatten child keys (overview, objectives, period) to root level
  // Also store in campaign object for UI detection
  if (chunk.campaign) {
    const campaign = chunk.campaign
    // Flatten to root level for API payload
    if (campaign.overview) result.overview = campaign.overview
    if (campaign.objectives) result.objectives = campaign.objectives
    if (campaign.period) result.period = campaign.period
    if (campaign.context) result.context = campaign.context
    // Also store in campaign object for UI detection (chunk.campaign exists check)
    result.campaign = { ...result.campaign, ...campaign }
  }
  
  // Process media_planner from API response -> store as media_plan in inputs
  // API uses 'media_planner' as top-level key, but we store as 'media_plan'
  // The data could be directly in media_planner OR nested inside media_planner.media_plan
  if (chunk.media_planner && typeof chunk.media_planner === 'object') {
    // Check if actual data is nested inside media_plan key
    if (chunk.media_planner.media_plan && typeof chunk.media_planner.media_plan === 'object') {
      result.media_plan = { ...result.media_plan, ...chunk.media_planner.media_plan }
    } else {
      // Data is directly in media_planner
      result.media_plan = { ...result.media_plan, ...chunk.media_planner }
    }
  }
  
  // Process sitelist from API response -> store sitelists in media_plan.sitelists
  // API structure: sitelist.media_plan.sitelists
  if (chunk.sitelist && typeof chunk.sitelist === 'object') {
    const sitelistData = chunk.sitelist.media_plan?.sitelists
    if (Array.isArray(sitelistData) && sitelistData.length > 0) {
      // Merge sitelists into media_plan
      result.media_plan = { 
        ...result.media_plan, 
        sitelists: sitelistData 
      }
    }
    // Also store the full sitelist response for site_list agent detection
    result.site_list = { ...result.site_list, ...chunk.sitelist }
  }
  
  // Process other agent results - store for UI display (not sent to API)
  // These are kept in state for UI but filtered out by sanitizeInputsForPayload
  const agentResultKeys = [
    'evaluation', 'creative_generation', 'template_recommender',
    'site_list', 'insights'
  ]
  
  for (const key of agentResultKeys) {
    if (chunk[key] && typeof chunk[key] === 'object') {
      result[key] = { ...result[key], ...chunk[key] }
    }
  }
  
  // Copy any other properties (for future extensibility)
  // Explicitly ignore keys that are handled above or shouldn't be stored
  const ignoredKeys = [
    'orchestrator', 'requirements', 'campaign', 'media_planner', 'sitelist', 'error', 
    ...agentResultKeys
  ]
  for (const key of Object.keys(chunk)) {
    if (!ignoredKeys.includes(key)) {
      result[key] = chunk[key]
    }
  }
  
  return result
}

// ============================================================================
// Streaming API
// ============================================================================

/**
 * Stream the full workflow API
 * Parses concatenated JSON objects (split on `}{` pattern)
 */
export async function streamFullWorkflow(
  userId: string,
  query: string,
  existingInputs: AccumulatedInputs,
  callbacks: StreamCallbacks
): Promise<void> {
  // Sanitize inputs to only include valid API keys
  const sanitizedInputs = sanitizeInputsForPayload(existingInputs)
  
  const payload = {
    user_id: userId,
    query: query,
    inputs: sanitizedInputs
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}/stream-full-workflow`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`)
    }
    
    if (!response.body) {
      throw new Error('No response body')
    }
    
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let accumulated = { ...existingInputs }
    
    while (true) {
      const { done, value } = await reader.read()
      
      if (done) break
      
      // Decode chunk and add to buffer
      buffer += decoder.decode(value, { stream: true })
      
      // Try to parse complete JSON objects from buffer
      const { chunks, remaining } = parseJsonBuffer(buffer)
      buffer = remaining
      
      // Process each parsed chunk
      for (const chunk of chunks) {
        accumulated = mergeChunkIntoInputs(accumulated, chunk)
        
        // Notify about chunk
        callbacks.onChunk(chunk, accumulated)
        
        // Notify about chat updates if chat_history changed
        if (chunk.orchestrator?.chat_history || chunk.requirements?.chat_history) {
          const chatHistory = accumulated.chat_history
          const latestMessage = chunk.orchestrator?.reply_to_human || ''
          callbacks.onChatUpdate(chatHistory, latestMessage)
        }
      }
    }
    
    // Process any remaining buffer content
    if (buffer.trim()) {
      try {
        const chunk = JSON.parse(buffer) as StreamChunk
        accumulated = mergeChunkIntoInputs(accumulated, chunk)
        callbacks.onChunk(chunk, accumulated)
      } catch (e) {
        console.warn('Failed to parse remaining buffer:', buffer)
      }
    }
    
    // Save final state and notify completion
    saveInputsToStorage(accumulated)
    callbacks.onComplete(accumulated)
    
  } catch (error) {
    callbacks.onError(error instanceof Error ? error : new Error(String(error)))
  }
}

/**
 * Parse concatenated JSON objects from buffer
 * Splits on `}{` pattern and returns parsed chunks + remaining buffer
 */
function parseJsonBuffer(buffer: string): { chunks: StreamChunk[], remaining: string } {
  const chunks: StreamChunk[] = []
  
  // Find complete JSON objects by tracking brace depth
  let depth = 0
  let start = 0
  let inString = false
  let escaped = false
  
  for (let i = 0; i < buffer.length; i++) {
    const char = buffer[i]
    
    if (escaped) {
      escaped = false
      continue
    }
    
    if (char === '\\') {
      escaped = true
      continue
    }
    
    if (char === '"') {
      inString = !inString
      continue
    }
    
    if (inString) continue
    
    if (char === '{') {
      if (depth === 0) start = i
      depth++
    } else if (char === '}') {
      depth--
      if (depth === 0) {
        // Found complete JSON object
        const jsonStr = buffer.slice(start, i + 1)
        try {
          const parsed = JSON.parse(jsonStr) as StreamChunk
          chunks.push(parsed)
        } catch (e) {
          console.warn('Failed to parse JSON chunk:', jsonStr.slice(0, 100))
        }
        start = i + 1
      }
    }
  }
  
  // Return remaining unparsed content
  const remaining = buffer.slice(start)
  return { chunks, remaining }
}

// ============================================================================
// Reply Message Extraction
// ============================================================================

/**
 * Get reply_to_human from orchestrator chunk
 * This is the primary source for AI replies to user
 */
export function getReplyToHuman(chunk: StreamChunk): string | null {
  if (chunk.orchestrator?.reply_to_human) {
    return chunk.orchestrator.reply_to_human
  }
  return null
}

/**
 * Get clarification from requirements chunk
 * Used when requirements agent needs more info from user
 */
export function getClarification(chunk: StreamChunk): string | null {
  if (chunk.requirements?.clarification) {
    return chunk.requirements.clarification
  }
  return null
}

// ============================================================================
// Agent Detection & Tab Visibility
// ============================================================================

/**
 * Check if an object has meaningful data
 */
function hasObjectData(obj: any): boolean {
  if (!obj || typeof obj !== 'object') return false
  
  return Object.values(obj).some(value => {
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0
    return value !== null && value !== undefined && value !== ''
  })
}

/**
 * Detect which agent results are available in inputs.
 * Checks both the streaming-path keys (template_recommender, site_list)
 * and the cable-path top-level keys (template_recommendations, sitelists, agentic_buy).
 */
export function detectAvailableAgents(inputs: AccumulatedInputs): {
  hasMediaPlan: boolean
  hasCreativeGeneration: boolean
  hasTemplateRecommender: boolean
  hasSiteList: boolean
  hasSalesRecommendation: boolean
  hasInsights: boolean
  hasResults: boolean
} {
  const hasMediaPlan = hasObjectData(inputs.media_plan)
  const hasCreativeGeneration = hasObjectData(inputs.creative_generation)
  const hasTemplateRecommender = hasObjectData(inputs.template_recommender)
    || hasObjectData(inputs.template_recommendations)
    || hasObjectData(inputs.template_recommendation)
  const hasSiteList = hasObjectData(inputs.site_list)
    || hasObjectData(inputs.sitelists)
  // Agentic-buy: gate the Media Buy tab on real products being present.
  // Field name `hasSalesRecommendation` retained so getVisibleTabs and any
  // downstream consumer keep working without a wider rename.
  const agenticBuy = inputs.agentic_buy
  const agenticBuyProducts = agenticBuy?.get_product?.products
  const agenticBuyCount = agenticBuy?.get_product?.count
  const hasSalesRecommendation =
    (Array.isArray(agenticBuyProducts) && agenticBuyProducts.length > 0) ||
    (typeof agenticBuyCount === 'number' && agenticBuyCount > 0)
  const hasInsights = hasObjectData(inputs.insights)

  const hasResults = hasMediaPlan || hasCreativeGeneration || hasTemplateRecommender ||
                     hasSiteList || hasSalesRecommendation || hasInsights

  return {
    hasMediaPlan,
    hasCreativeGeneration,
    hasTemplateRecommender,
    hasSiteList,
    hasSalesRecommendation,
    hasInsights,
    hasResults
  }
}

/**
 * Get visible tabs based on available agent data.
 * When isOrchestratorRunning is true the proposal tab is always shown
 * so the user can see loading indicators while data streams in.
 */
export function getVisibleTabs(inputs: AccumulatedInputs, isOrchestratorRunning = false): TabVisibility {
  const agents = detectAvailableAgents(inputs)
  
  return {
    proposal: agents.hasMediaPlan || agents.hasSiteList || agents.hasTemplateRecommender || isOrchestratorRunning,
    creative: agents.hasCreativeGeneration,
    mediaBuy: agents.hasSalesRecommendation,
    insights: agents.hasInsights
  }
}

/**
 * Get visible proposal sub-tabs.
 * When isOrchestratorRunning is true, show all core sub-tabs so they
 * can display loading skeletons until data arrives.
 */
export function getVisibleProposalSubTabs(inputs: AccumulatedInputs, isOrchestratorRunning = false): ProposalSubTabVisibility {
  const agents = detectAvailableAgents(inputs)
  const hasSiteListInMediaPlan = inputs.media_plan?.sitelists?.length > 0
  
  return {
    mediaPlan: agents.hasMediaPlan || isOrchestratorRunning,
    siteList: agents.hasSiteList || hasSiteListInMediaPlan || isOrchestratorRunning,
    templates: agents.hasTemplateRecommender || isOrchestratorRunning,
    audience: agents.hasMediaPlan
  }
}

/**
 * Get label for "View Results" button based on available data
 */
export function getResultsButtonLabel(inputs: AccumulatedInputs): string {
  const agents = detectAvailableAgents(inputs)
  
  if (agents.hasMediaPlan) return 'View Media Plan'
  if (agents.hasCreativeGeneration) return 'View Creatives'
  if (agents.hasTemplateRecommender) return 'View Templates'
  if (agents.hasSiteList) return 'View Site List'
  if (agents.hasInsights) return 'View Insights'

  return 'View Results'
}

// ============================================================================
// Formatting Helpers
// ============================================================================

/**
 * Format campaign details (overview, objectives, period) for display
 * Checks both root level and campaign object for these fields
 */
export function formatCampaignDetails(inputs: AccumulatedInputs): string {
  const sections: string[] = []
  
  // Check campaign object first, then fall back to root level
  const campaign = inputs.campaign || {}
  const overview = campaign.overview || inputs.overview
  const objectives = campaign.objectives || inputs.objectives
  const period = campaign.period || inputs.period
  
  if (overview?.trim()) {
    sections.push(`**Overview**\n${overview.trim()}`)
  }
  
  if (objectives?.length > 0) {
    const list = objectives.map((obj: string) => `• ${obj}`).join('\n')
    sections.push(`**Objectives**\n${list}`)
  }
  
  if (period?.trim()) {
    sections.push(`**Campaign Period**\n${period.trim()}`)
  }
  
  return sections.join('\n\n')
}

/**
 * Check if campaign object has data (overview, objectives, period)
 */
export function hasCampaignData(inputs: AccumulatedInputs): boolean {
  const campaign = inputs.campaign || {}
  const overview = campaign.overview || inputs.overview
  const objectives = campaign.objectives || inputs.objectives
  const period = campaign.period || inputs.period
  
  return !!(overview?.trim() || objectives?.length > 0 || period?.trim())
}

/**
 * Build AI response content for chat display
 * Uses reply_to_human as primary source
 * Shows overview/objectives/period from campaign object until media_plan is detected
 */
export function buildAiResponseContent(inputs: AccumulatedInputs): string {
  const agents = detectAvailableAgents(inputs)
  
  // Use reply_to_human as the primary response, fallback to clarification
  let response = inputs.reply_to_human || inputs.clarification || ''
  
  // Add campaign details only if no media_plan yet and we have campaign data
  if (!agents.hasMediaPlan && hasCampaignData(inputs)) {
    const details = formatCampaignDetails(inputs)
    if (details) {
      response = response ? response + '\n\n' + details : details
    }
  }
  
  return response || 'I understand your request. Let me help you with that.'
}
